import React, { memo, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Accordion, AccordionActions, AccordionDetails, AccordionSummary, Box, Typography, Divider } from "@material-ui/core";
import { currentStepSelector, formSummarySelector } from '../../../store/modules/steps/selectors';
import { CustomSelectType, CustomSelectTypeOptions, FormStep, FormStepProps, StepsState } from '../../../model/global';
import { getFormControlInitialState, getFormSummaryField } from '../../utils';
import { validateSerialOrOrderNumber } from "../../../store/modules/steps/thunks";
import { CustomFormFieldState } from '../../FormControls/CustomFormField/CustomFormField';
import { translationsSelector } from "../../../store/modules/translations/selectors";
import { specialUseCaseFields } from "../../../../mocks/utils";
import { AccordionArrayAction } from "../../../../../o-heating-form/react/model/global";
import { stepsActions } from '../../../store/modules/steps/reducer';
import CustomFormField from '../../FormControls/CustomFormField';
import Button from 'global/react-mui/atoms/Button';

const MainEnquiry: React.FC<FormStepProps> = ({ onStepControlsChange, data: { stepTemplate } }) => {
	const dispatch = useDispatch()
	const step = useSelector(currentStepSelector) as FormStep<'main'>
	const {
		addProductButtonText,
		removeProductButtonText,
		validateSerialOrOrderNumberButtonText,
		secondProduct,
		thirdProduct,
		fourthProduct,
		fifthProduct
	} = useSelector(translationsSelector)
	const summary = useSelector(formSummarySelector) as StepsState['formSummary']
	const productFieldsState = step.stepData.accordionArray;
	const stepErrors = summary.validationErrors[step.stepTemplate];
	const accordionFieldPadding = { paddingLeft: '10px' };

	const fields: {
		enquiryTopic: CustomFormFieldState | undefined,
		firstPossibleMaintenance: CustomFormFieldState | undefined,

		productCategory: CustomFormFieldState | undefined,
		productRelated: CustomFormFieldState | undefined,
		serialOrOrderNumber: CustomFormFieldState | undefined,
		installationYear: CustomFormFieldState | undefined,
		lastMaintenance: CustomFormFieldState | undefined

		productCategoryTwo: CustomFormFieldState | undefined,
		productRelatedTwo: CustomFormFieldState | undefined,
		serialOrOrderNumberTwo: CustomFormFieldState | undefined,
		installationYearTwo: CustomFormFieldState | undefined,
		lastMaintenanceTwo: CustomFormFieldState | undefined

		productCategoryThree: CustomFormFieldState | undefined,
		productRelatedThree: CustomFormFieldState | undefined,
		serialOrOrderNumberThree: CustomFormFieldState | undefined,
		installationYearThree: CustomFormFieldState | undefined,
		lastMaintenanceThree: CustomFormFieldState | undefined

		productCategoryFour: CustomFormFieldState | undefined,
		productRelatedFour: CustomFormFieldState | undefined,
		serialOrOrderNumberFour: CustomFormFieldState | undefined,
		installationYearFour: CustomFormFieldState | undefined,
		lastMaintenanceFour: CustomFormFieldState | undefined,

		productCategoryFive: CustomFormFieldState | undefined,
		productRelatedFive: CustomFormFieldState | undefined,
		serialOrOrderNumberFive: CustomFormFieldState | undefined,
		installationYearFive: CustomFormFieldState | undefined,
		lastMaintenanceFive: CustomFormFieldState | undefined
	} = useMemo(
		() => {
			return {
				enquiryTopic: getFormControlInitialState('enquiry_topic', step.stepData.fields.main),

				firstPossibleMaintenance: getFormControlInitialState('first_possible_maintenance', step.stepData.fields.main),

				productCategory: getFormControlInitialState('product_category_1', step.stepData.fields.main),
				serialOrOrderNumber: getFormControlInitialState('serial_or_order_number_1', step.stepData.fields.main),
				installationYear: getFormControlInitialState('installation_year_1', step.stepData.fields.main),
				productRelated: getFormControlInitialState('product_related_1', step.stepData.fields.main),
				lastMaintenance: getFormControlInitialState('last_maintenance_1', step.stepData.fields.main),

				productCategoryTwo: getFormControlInitialState('product_category_2', step.stepData.fields.main),
				serialOrOrderNumberTwo: getFormControlInitialState('serial_or_order_number_2', step.stepData.fields.main),
				installationYearTwo: getFormControlInitialState('installation_year_2', step.stepData.fields.main),
				productRelatedTwo: getFormControlInitialState('product_related_2', step.stepData.fields.main),
				lastMaintenanceTwo: getFormControlInitialState('last_maintenance_2', step.stepData.fields.main),

				productCategoryThree: getFormControlInitialState('product_category_3', step.stepData.fields.main),
				serialOrOrderNumberThree: getFormControlInitialState('serial_or_order_number_3', step.stepData.fields.main),
				installationYearThree: getFormControlInitialState('installation_year_3', step.stepData.fields.main),
				productRelatedThree: getFormControlInitialState('product_related_3', step.stepData.fields.main),
				lastMaintenanceThree: getFormControlInitialState('last_maintenance_3', step.stepData.fields.main),

				productCategoryFour: getFormControlInitialState('product_category_4', step.stepData.fields.main),
				productRelatedFour: getFormControlInitialState('product_related_4', step.stepData.fields.main),
				serialOrOrderNumberFour: getFormControlInitialState('serial_or_order_number_4', step.stepData.fields.main),
				installationYearFour: getFormControlInitialState('installation_year_4', step.stepData.fields.main),
				lastMaintenanceFour: getFormControlInitialState('last_maintenance_4', step.stepData.fields.main),

				productCategoryFive: getFormControlInitialState('product_category_5', step.stepData.fields.main),
				productRelatedFive: getFormControlInitialState('product_related_5', step.stepData.fields.main),
				serialOrOrderNumberFive: getFormControlInitialState('serial_or_order_number_5', step.stepData.fields.main),
				installationYearFive: getFormControlInitialState('installation_year_5', step.stepData.fields.main),
				lastMaintenanceFive: getFormControlInitialState('last_maintenance_5', step.stepData.fields.main),

			}
		},
		[step.stepData.fields.main]
	);

	// initialise the step data once
	useEffect(() => {
		// don't re-init fields
		if (summary.initialized[stepTemplate]) return;

		const initialData: Record<string, CustomFormFieldState> = {};

		// fill in state with initial field values and validity
		for (const [key, element] of Object.entries(fields)) {
			if (!element || !element.name) continue;

			const isNameInSpecialUseCaseArray = specialUseCaseFields.includes(element.name);
			const invalid = element.value === '' && element.required

			initialData[element.name] = {
				name: element.name as string,
				originalField: element.originalField,
				value: element.value,
				required: isNameInSpecialUseCaseArray ? false : element.required,
				valid: isNameInSpecialUseCaseArray ? true : !invalid,
				invalidReason: element.invalidReason || '',
			};
		}

		dispatch(stepsActions.initializeStepPayloadValues({
			stepTemplate,
			stepPayload: initialData,
			initialize: true,
		}));
	}, [dispatch, fields, stepTemplate, summary.initialized]);// eslint-disable-line


	const fieldChangeHandler = (event): void => {
		if (event?.name?.indexOf('serial_or_order_number_') === 0) {
			const isValidNumber = [16, 18, 24].includes(event.value?.length); //FE VALIDATION
			event.valid = isValidNumber;
			if (summary.validatedSerialOrOrderNumber && isValidNumber) dispatch(validateSerialOrOrderNumber());
		}
		dispatch(stepsActions.updateStepPayloadValues({
			stepTemplate,
			stepPayload: event,
		}));
	}

	const categoryChangeHandler = (selectField: CustomFormFieldState): void => {
		const field = selectField.originalField as CustomSelectType;
		let productRelatedName: string;
		switch (field.name) {
			case 'product_category_2':
				productRelatedName = 'product_related_2';
				break;
			case 'product_category_3':
				productRelatedName = 'product_related_3';
				break
			case 'product_category_4':
				productRelatedName = 'product_related_4';
				break
			case 'product_category_5':
				productRelatedName = 'product_related_5';
				break
			default:
				productRelatedName = 'product_related_1';

		}
		const groupItem = field.optionsGroup
			?.map(o => o.options)
			?.find(g => {
				if (!g) return undefined
				const exists = g.findIndex(o => o.value === selectField.value)
				return exists > -1 ? g[exists] : undefined
			})

		const optionItems: CustomSelectTypeOptions[] | undefined = groupItem && groupItem.filter(g => g.value === selectField.value)
		if (!optionItems) return

		// handle required state for serial and order numbers

		// get controls from redux
		const relatedProductField = getFormSummaryField(summary, step.stepTemplate, productRelatedName);

		// make a copy as redux values are readonly
		const newRelatedProductField = JSON.parse(JSON.stringify(relatedProductField)) as CustomFormFieldState
		if (!relatedProductField) return;

		// build the list of options for the related product dropdown
		const relatedProductOptions = field.options.filter(o => o.groupParent === selectField.value);

		newRelatedProductField.options = relatedProductOptions
		newRelatedProductField.value = relatedProductOptions.length > 0 ? relatedProductOptions[0].value : '' // set the value to the first item
		newRelatedProductField.valid = newRelatedProductField.value !== '' // update validity

		dispatch(stepsActions.updateStepPayloadValues({
			stepTemplate,
			stepPayload: [selectField, newRelatedProductField],
		}));

	}

	if (!step) return null

	const sumStepTemp = (item: string) => summary.steps[stepTemplate][item]

	const enquiryTopic = sumStepTemp('enquiry_topic')
		? sumStepTemp('enquiry_topic')
		: undefined;
	const firstPossibleMaintenance = sumStepTemp('first_possible_maintenance')
		? sumStepTemp('first_possible_maintenance')
		: undefined;

	const productCategory = sumStepTemp('product_category_1')
		? sumStepTemp('product_category_1')
		: undefined;
	const serialOrOrderNumber = sumStepTemp('serial_or_order_number_1')
		? sumStepTemp('serial_or_order_number_1')
		: undefined;
	const installationYear = sumStepTemp('installation_year_1')
		? sumStepTemp('installation_year_1')
		: undefined;
	const productRelated = sumStepTemp('product_related_1')
		? sumStepTemp('product_related_1')
		: undefined;
	const lastMaintenance = sumStepTemp('last_maintenance_1')
		? sumStepTemp('last_maintenance_1')
		: undefined;


	const productCategoryTwo = sumStepTemp('product_category_2')
		? sumStepTemp('product_category_2')
		: undefined;
	const serialOrOrderNumberTwo = sumStepTemp('serial_or_order_number_2')
		? sumStepTemp('serial_or_order_number_2')
		: undefined;
	const installationYearTwo = sumStepTemp('installation_year_2')
		? sumStepTemp('installation_year_2')
		: undefined;
	const productRelatedTwo = sumStepTemp('product_related_2')
		? sumStepTemp('product_related_2')
		: undefined;
	const lastMaintenanceTwo = sumStepTemp('last_maintenance_2')
		? sumStepTemp('last_maintenance_2')
		: undefined;


	const productCategoryThree = sumStepTemp('product_category_3')
		? sumStepTemp('product_category_3')
		: undefined;
	const serialOrOrderNumberThree = sumStepTemp('serial_or_order_number_3')
		? sumStepTemp('serial_or_order_number_3')
		: undefined;
	const installationYearThree = sumStepTemp('installation_year_3')
		? sumStepTemp('installation_year_3')
		: undefined;
	const productRelatedThree = sumStepTemp('product_related_3')
		? sumStepTemp('product_related_3')
		: undefined;
	const lastMaintenanceThree = sumStepTemp('last_maintenance_3')
		? sumStepTemp('last_maintenance_3')
		: undefined;

	const productCategoryFour = sumStepTemp('product_category_4')
		? sumStepTemp('product_category_4')
		: undefined;
	const serialOrOrderNumberFour = sumStepTemp('serial_or_order_number_4')
		? sumStepTemp('serial_or_order_number_4')
		: undefined;
	const installationYearFour = sumStepTemp('installation_year_4')
		? sumStepTemp('installation_year_4')
		: undefined;
	const productRelatedFour = sumStepTemp('product_related_4')
		? sumStepTemp('product_related_4')
		: undefined;
	const lastMaintenanceFour = sumStepTemp('last_maintenance_4')
		? sumStepTemp('last_maintenance_4')
		: undefined;

	const productCategoryFive = sumStepTemp('product_category_5')
		? sumStepTemp('product_category_5')
		: undefined;
	const serialOrOrderNumberFive = sumStepTemp('serial_or_order_number_5')
		? sumStepTemp('serial_or_order_number_5')
		: undefined;
	const installationYearFive = sumStepTemp('installation_year_5')
		? sumStepTemp('installation_year_5')
		: undefined;
	const productRelatedFive = sumStepTemp('product_related_5')
		? sumStepTemp('product_related_5')
		: undefined;
	const lastMaintenanceFive = sumStepTemp('last_maintenance_5')
		? sumStepTemp('last_maintenance_5')
		: undefined;


	const attributeOBJ = (item: any, handler: any) => {
		return ({
			field: item,
			fieldErrors: stepErrors,
			onFieldChange: (e: any) => handler(e)
		})
	}


	const handleAddAnotherProduct = () => {
		const productFieldsArrayCopy = [...productFieldsState!];
		let fieldsArray: CustomFormFieldState[] = [];
		let fieldArrayCopy: any = [];


		for (let i = 0; i < productFieldsArrayCopy.length; i++) {
			if (productFieldsArrayCopy[i].added) {
				continue;
			}
			//close the previous product
			productFieldsArrayCopy[i - 1] = { ...productFieldsArrayCopy[i - 1], added: true, opened: false };
			//open it
			productFieldsArrayCopy[i] = { ...productFieldsArrayCopy[i], added: true, opened: true };

			//set required to true for all the fields, initially they are not required
			switch (productFieldsArrayCopy[i].panel) {
				case 1:
					fieldArrayCopy = [
						{ ...productCategoryTwo, valid: false, required: true },
						{ ...productRelatedTwo, valid: false, required: true },
						{ ...serialOrOrderNumberTwo, valid: false, required: true },
						{ ...installationYearTwo, valid: false, required: true },
						{ ...lastMaintenanceTwo, valid: true, required: false }
					];
					fieldsArray = fieldArrayCopy
					break;
				case 2:
					fieldArrayCopy = [
						{ ...productCategoryThree, valid: false, required: true },
						{ ...productRelatedThree, valid: false, required: true },
						{ ...serialOrOrderNumberThree, valid: false, required: true },
						{ ...installationYearThree, valid: false, required: true },
						{ ...lastMaintenanceThree, valid: true, required: false }
					];
					fieldsArray = fieldArrayCopy
					break;
				case 3:
					fieldArrayCopy = [
						{ ...productCategoryFour, valid: false, required: true },
						{ ...productRelatedFour, valid: false, required: true },
						{ ...serialOrOrderNumberFour, valid: false, required: true },
						{ ...installationYearFour, valid: false, required: true },
						{ ...lastMaintenanceFour, valid: true, required: false }
					];
					fieldsArray = fieldArrayCopy
					break;
				case 4:
					fieldArrayCopy = [
						{ ...productCategoryFive, valid: false, required: true },
						{ ...productRelatedFive, valid: false, required: true },
						{ ...serialOrOrderNumberFive, valid: false, required: true },
						{ ...installationYearFive, valid: false, required: true },
						{ ...lastMaintenanceFive, valid: true, required: false }
					];
					fieldsArray = fieldArrayCopy
					break;
			}
			break;
		}
		fieldChangeHandler(fieldsArray)
		dispatch(stepsActions.updateAccordionArray({
			stepPayload: productFieldsArrayCopy,
			action: AccordionArrayAction.Add
		}));
	}

	const removeProduct = (panel: number) => {
		let fieldsArray: CustomFormFieldState[] = [];
		let fieldArrayCopy: any = [];
		const productFieldsArrayCopy = [...productFieldsState!]
		const index = productFieldsArrayCopy.findIndex(element => element.panel === panel);
		productFieldsArrayCopy[index] = { ...productFieldsArrayCopy[index], opened: false, added: false };

		switch (panel) {
			case 1:
				fieldArrayCopy = [
					{ ...productCategoryTwo, value: '', valid: true, required: false },
					{ ...productRelatedTwo, value: '', valid: true, required: false },
					{ ...serialOrOrderNumberTwo, value: '', valid: true, required: false },
					{ ...installationYearTwo, value: '', valid: true, required: false },
					{ ...lastMaintenanceTwo, value: '', valid: true, required: false }
				];
				fieldsArray = fieldArrayCopy
				break;
			case 2:
				fieldArrayCopy = [
					{ ...productCategoryThree, value: '', valid: true, required: false },
					{ ...productRelatedThree, value: '', valid: true, required: false },
					{ ...serialOrOrderNumberThree, value: '', valid: true, required: false },
					{ ...installationYearThree, value: '', valid: true, required: false },
					{ ...lastMaintenanceThree, value: '', valid: true, required: false }
				];
				fieldsArray = fieldArrayCopy
				break;
			case 3:
				fieldArrayCopy = [
					{ ...productCategoryFour, value: '', valid: true, required: false },
					{ ...productRelatedFour, value: '', valid: true, required: false },
					{ ...serialOrOrderNumberFour, value: '', valid: true, required: false },
					{ ...installationYearFour, value: '', valid: true, required: false },
					{ ...lastMaintenanceFour, value: '', valid: true, required: false }
				];
				fieldsArray = fieldArrayCopy
				break;
			case 4:
				fieldArrayCopy = [
					{ ...productCategoryFive, value: '', valid: true, required: false },
					{ ...productRelatedFive, value: '', valid: true, required: false },
					{ ...serialOrOrderNumberFive, value: '', valid: true, required: false },
					{ ...installationYearFive, value: '', valid: true, required: false },
					{ ...lastMaintenanceFive, value: '', valid: true, required: false }
				];
				fieldsArray = fieldArrayCopy
				break;
		}
		fieldChangeHandler(fieldsArray);
		dispatch(stepsActions.updateAccordionArray({
			stepPayload: productFieldsArrayCopy,
			action: AccordionArrayAction.Remove
		}));
		dispatch(validateSerialOrOrderNumber()); 123
	}

	const handleAccordionChange = (panel: number) => (event, isExpanded) => {
		const arrayCopy = [...productFieldsState!];
		const index = arrayCopy.findIndex(element => element.panel === panel);
		if (index === -1) return;

		arrayCopy[index] = { ...arrayCopy[index], opened: !arrayCopy[index].opened };
		dispatch(stepsActions.updateAccordionArray({
			stepPayload: arrayCopy,
			action: AccordionArrayAction.OpenClose
		}));
	};

	const AddNewProductComponent = (): JSX.Element => {
		let element;
		if (productFieldsState)
			element = productFieldsState.find(element => element?.panel === productFieldsState.length);
		if (!element.added)

			return (
				<Box margin={0} sx={{ marginTop: 48 }} className={'uk-width-1-1'}>
					<div className="add-product-component__dashed-box uk-flex uk-flex-center uk-flex-middle"
						data-ref="add-product-component__button">
						<div className="uk-flex uk-flex-middle"
							onClick={() => {
								summary.validatedSerialOrOrderNumber
									? handleAddAnotherProduct()
									: dispatch(validateSerialOrOrderNumber())
							}}>
							{summary.validatedSerialOrOrderNumber
								? <React.Fragment>
									<span className={"add-product-component__gray-circle-button"}>
										<span className="add-product-component__icon">
											<svg version="1.1" xmlns="http://www.w3.org/2000/svg" x="0" y="0"
												viewBox="0 0 512 512"><polygon
													fill="currentColor"
													points="512,226.5 285.5,226.5 285.5,0 226.5,0 226.5,226.5 0,226.5 0,285.5 226.5,285.5 226.5,512 285.5,512 285.5,285.5 512,285.5" /></svg>
										</span>
									</span>
									<span className="add-product-component__text">{addProductButtonText}</span>
								</React.Fragment>
								: <span className="add-product-component__text">
									{validateSerialOrOrderNumberButtonText}
								</span>
							}
						</div>
					</div>
				</Box>
			)
		return <span />
	}


	return (
		<div className='uk-grid-medium uk-grid uk-grid-stack'>
			{/* Two select boxes like on here: https://www.wolf.eu/service/kundendienstbeauftragung/ */}
			{enquiryTopic && <CustomFormField  {...attributeOBJ(enquiryTopic, fieldChangeHandler)} />}
			{firstPossibleMaintenance &&
				<CustomFormField  {...attributeOBJ(firstPossibleMaintenance, fieldChangeHandler)} />}

			{productCategory && <CustomFormField  {...attributeOBJ(productCategory, categoryChangeHandler)} />}
			{productRelated && <CustomFormField  {...attributeOBJ(productRelated, fieldChangeHandler)} />}
			{serialOrOrderNumber &&
				<CustomFormField  {...attributeOBJ(serialOrOrderNumber, fieldChangeHandler)} />}
			{installationYear && <CustomFormField  {...attributeOBJ(installationYear, fieldChangeHandler)} />}
			{lastMaintenance && <CustomFormField  {...attributeOBJ(lastMaintenance, fieldChangeHandler)} />}


			{productFieldsState && productFieldsState.find(element => element.panel === 1)!.added && (
				<div className={'uk-width-1-1 accordion-space'}>
					<Accordion expanded={productFieldsState!.find(element => element.panel === 1)!.opened}
						onChange={handleAccordionChange(1)}
						style={{ width: '100%' }}>
						<AccordionSummary
							expandIcon={<i className='chevron-down' />}
							aria-controls="panel1bh-content"
							id="panel1bh-header">
							<Typography>{secondProduct}</Typography>
						</AccordionSummary>
						<AccordionDetails style={{ flexWrap: "wrap" }}>
							{productCategoryTwo && <CustomFormField  {...attributeOBJ(productCategoryTwo, categoryChangeHandler)} />}
							{productRelatedTwo && <CustomFormField  {...attributeOBJ(productRelatedTwo, fieldChangeHandler)} customStyle={accordionFieldPadding} />}
							{serialOrOrderNumberTwo && <CustomFormField  {...attributeOBJ(serialOrOrderNumberTwo, fieldChangeHandler)} customStyle={accordionFieldPadding} />}
							{installationYearTwo && <CustomFormField  {...attributeOBJ(installationYearTwo, fieldChangeHandler)} customStyle={accordionFieldPadding} />}
							{lastMaintenanceTwo && <CustomFormField  {...attributeOBJ(lastMaintenanceTwo, fieldChangeHandler)} customStyle={accordionFieldPadding} />}
						</AccordionDetails>
						{/*Only the last added can be deleted*/}
						{productFieldsState && !productFieldsState.find(element => element.panel === 2)!.added && (
							<React.Fragment>
								<Divider />
								<AccordionActions>
									<Button {
										...{
											onClick: () => removeProduct(1),
											variant: "outlined",
											size: "small",
											className: "btn-next"
										}
									}>
										{removeProductButtonText}
									</Button>
								</AccordionActions>
							</React.Fragment>
						)
						}
					</Accordion>
				</div>
			)}

			{productFieldsState && productFieldsState!.find(element => element.panel === 2)!.added && (
				<div className={'uk-width-1-1 accordion-space'}>
					<Accordion
						expanded={productFieldsState && productFieldsState.find(element => element.panel === 2)!.opened}
						onChange={handleAccordionChange(2)}
						style={{ width: '100%' }}>
						<AccordionSummary
							expandIcon={<i className='chevron-down' />}
							aria-controls="panel1bh-content"
							id="panel1bh-header">
							<Typography>{thirdProduct}</Typography>

						</AccordionSummary>
						<AccordionDetails style={{ flexWrap: "wrap" }}>
							{productCategoryThree &&
								<CustomFormField  {...attributeOBJ(productCategoryThree, categoryChangeHandler)}
									customStyle={accordionFieldPadding} />}
							{productRelatedThree &&
								<CustomFormField  {...attributeOBJ(productRelatedThree, fieldChangeHandler)}
									customStyle={accordionFieldPadding} />}
							{serialOrOrderNumberThree &&
								<CustomFormField  {...attributeOBJ(serialOrOrderNumberThree, fieldChangeHandler)}
									customStyle={accordionFieldPadding} />}
							{installationYearThree &&
								<CustomFormField  {...attributeOBJ(installationYearThree, fieldChangeHandler)}
									customStyle={accordionFieldPadding} />}
							{lastMaintenanceThree &&
								<CustomFormField  {...attributeOBJ(lastMaintenanceThree, fieldChangeHandler)}
									customStyle={accordionFieldPadding} />}
						</AccordionDetails>
						{/*Only the last added can be deleted*/}
						{productFieldsState && !productFieldsState.find(element => element.panel === 3)!.added && (
							<React.Fragment>
								<Divider />
								<AccordionActions>
									<Button {
										...{
											onClick: () => removeProduct(2),
											variant: "outlined",
											size: "small",
											className: "btn-next"
										}
									}>
										{removeProductButtonText}
									</Button>
								</AccordionActions>
							</React.Fragment>
						)}
					</Accordion>
				</div>
			)}

			{productFieldsState && productFieldsState.find(element => element.panel === 3)!.added && (
				<div className={'uk-width-1-1 accordion-space'}>
					<Accordion
						expanded={productFieldsState && productFieldsState.find(element => element.panel === 3)!.opened}
						onChange={handleAccordionChange(3)}
						style={{ width: '100%' }}>
						<AccordionSummary
							expandIcon={<i className='chevron-down' />}
							aria-controls="panel1bh-content"
							id="panel1bh-header">
							<Typography>{fourthProduct}</Typography>
						</AccordionSummary>
						<AccordionDetails style={{ flexWrap: "wrap" }}>
							{productCategoryFour &&
								<CustomFormField  {...attributeOBJ(productCategoryFour, categoryChangeHandler)}
									customStyle={accordionFieldPadding} />}
							{productRelatedFour &&
								<CustomFormField  {...attributeOBJ(productRelatedFour, fieldChangeHandler)}
									customStyle={accordionFieldPadding} />}
							{serialOrOrderNumberFour &&
								<CustomFormField  {...attributeOBJ(serialOrOrderNumberFour, fieldChangeHandler)}
									customStyle={accordionFieldPadding} />}
							{installationYearFour &&
								<CustomFormField  {...attributeOBJ(installationYearFour, fieldChangeHandler)}
									customStyle={accordionFieldPadding} />}
							{lastMaintenanceFour &&
								<CustomFormField  {...attributeOBJ(lastMaintenanceFour, fieldChangeHandler)}
									customStyle={accordionFieldPadding} />}
						</AccordionDetails>
						<Divider />
						<AccordionActions>
							<Button {
								...{
									onClick: () => removeProduct(3),
									variant: "outlined",
									size: "small",
									className: "btn-next"
								}
							}>
								{removeProductButtonText}
							</Button>
						</AccordionActions>
					</Accordion>
				</div>
			)}

			{productFieldsState && productFieldsState.find(element => element.panel === 4)!.added && (
				<div className={'uk-width-1-1 accordion-space'}>
					<Accordion
						expanded={productFieldsState && productFieldsState.find(element => element.panel === 4)!.opened}
						onChange={handleAccordionChange(4)}
						style={{ width: '100%' }}>
						<AccordionSummary
							expandIcon={<i className='chevron-down' />}
							aria-controls="panel1bh-content"
							id="panel1bh-header">
							<Typography>{fifthProduct}</Typography>
						</AccordionSummary>
						<AccordionDetails style={{ flexWrap: "wrap" }}>
							{productCategoryFive &&
								<CustomFormField  {...attributeOBJ(productCategoryFive, categoryChangeHandler)}
									customStyle={accordionFieldPadding} />}
							{productRelatedFive &&
								<CustomFormField  {...attributeOBJ(productRelatedFive, fieldChangeHandler)}
									customStyle={accordionFieldPadding} />}
							{serialOrOrderNumberFive &&
								<CustomFormField  {...attributeOBJ(serialOrOrderNumberFive, fieldChangeHandler)}
									customStyle={accordionFieldPadding} />}
							{installationYearFive &&
								<CustomFormField  {...attributeOBJ(installationYearFive, fieldChangeHandler)}
									customStyle={accordionFieldPadding} />}
							{lastMaintenanceFive &&
								<CustomFormField  {...attributeOBJ(lastMaintenanceFive, fieldChangeHandler)}
									customStyle={accordionFieldPadding} />}
						</AccordionDetails>
						<Divider />
						<AccordionActions>
							<Button {
								...{
									onClick: () => removeProduct(4),
									variant: "outlined",
									size: "small",
									className: "btn-next"
								}
							}>
								{removeProductButtonText}
							</Button>
						</AccordionActions>
					</Accordion>
				</div>
			)}

			{productFieldsState && <AddNewProductComponent />}
		</div>
	);
}
	;

export default memo(MainEnquiry);
